/**
 * Merging a deck's NOTES / MIND MAPS index (`SyncDeckMeta.materials`).
 *
 * The deck meta row is last-write-wins as a whole, and that is fine for a
 * name or a colour: losing the older of two renames loses a word. It is not
 * fine for the materials index, because the index is the only thing that
 * makes a note REACHABLE — a note whose id nobody lists still has its Y.Doc,
 * and no screen will ever open it again. So two rows that meet anywhere
 * (`deckProperties.mergeEntry`: the UserDo and StudyGroupDo push paths, and
 * the client's reconnect diff when its own row is dirty) keep the winner's
 * every other field and UNION the index by id:
 *
 *   - an entry only one side lists survives — the phone's offline "Note 2"
 *     lands beside the map the web renamed meanwhile;
 *   - the ORDER is the winner's list, then what only the loser lists — the
 *     same on every node, so unkeyed notes read in one order everywhere
 *     (a clock tie is broken by content, not by argument order);
 *   - an entry both sides list takes the version with the newer per-entry
 *     clock (`updatedAtISO`, `addedAtISO` until the first edit), ties to
 *     the row's winner — a rename on one device and a drag on the other
 *     resolve per entry, not per row;
 *   - a tombstone (`deletedAtISO`) is sticky: once either side removed the
 *     item, no stale copy brings it back, whatever its clock says. The
 *     earliest tombstone is kept so the merge is order-independent.
 *
 * The deck's documents (`sources`) are unioned the same way, with removals
 * kept as `sourceTombstones` beside the list — and there a removal is
 * outranked only by the entry's birth or an Undo, never by an edit (see
 * `mergeDeckSourceList`). Which document is FIRST is not decided by the
 * order at all but by its own clock (`sourcePrimaryStamps`, see
 * `stampedFirst`), because that is the document every card ref without a
 * document id reads.
 *
 * Pure and dependency-free: it runs unmodified in a Worker isolate, Hermes
 * and jsdom. Commutative up to ties and idempotent — pinned by the spec.
 */

import { isPlaceholderSource, sourceAliveClock } from '../../deck/sources/manage';
import { mergedDocumentLength } from '../../deck/sources/text-length';
import type {
  DeckMaterialsIndex,
  DeckMindmapItem,
  DeckNoteItem,
  DeckSource,
  DeckSourcePrimaryStamps,
  DeckSourceTombstones,
} from '../schema/shared-types';

type MaterialEntry = {
  addedAtISO: string;
  updatedAtISO?: string;
  deletedAtISO?: string;
  sortKey?: string;
  movedAtISO?: string;
  /** Documents only (`DeckSource.restoredAtISO`). */
  restoredAtISO?: string;
  /** Documents only — the file a `textLength` was measured over. */
  pdfInfo?: DeckSource['pdfInfo'];
  /** Documents only (`DeckSource.textLength`). */
  textLength?: DeckSource['textLength'];
};

/** The clock an entry is compared on: its own edit clock, else its birth. */
export function materialEntryClock(entry: MaterialEntry): string {
  return entry.updatedAtISO ?? entry.addedAtISO ?? '';
}

function earliest(a: string | undefined, b: string | undefined): string | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return a <= b ? a : b;
}

function latest(a: string | undefined, b: string | undefined): string | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return a >= b ? a : b;
}

/**
 * One entry both sides hold. The entry's content (name, provenance) follows
 * its edit clock; its PLACE (`sortKey`) follows its own clock (`movedAtISO`),
 * because a drag keys every entry the deck had never placed — a first drag
 * on one device and a rename on another would otherwise meet as two whole-
 * entry writes, and the newer one (the drag, name untouched) would discard
 * the rename. Ties on either clock go to `incoming`, the row's winner.
 *
 * A document's restore clock (`restoredAtISO`) is the later of both copies
 * whoever wins the content: a copy that never saw the Undo (a rename made
 * elsewhere, an older build's write-back) must not erase it.
 *
 * A document's measured length (`textLength`) is derived from the winning
 * content, so it comes from whichever copy holds one that still matches the
 * winner's file and pages (`mergedDocumentLength`): recording it never stamps
 * the edit clock, and a rename that had not seen it cannot drop it.
 */
function mergeMaterialEntry<T extends MaterialEntry>(base: T, incoming: T): T {
  const incomingWins = materialEntryClock(incoming) >= materialEntryClock(base);
  const winner = incomingWins ? incoming : base;
  const loser = incomingWins ? base : incoming;
  let out = winner;
  if ((loser.movedAtISO ?? '') > (winner.movedAtISO ?? '')) {
    out = { ...out, sortKey: loser.sortKey, movedAtISO: loser.movedAtISO };
  }
  const deletedAtISO = earliest(base.deletedAtISO, incoming.deletedAtISO);
  if (deletedAtISO !== undefined && out.deletedAtISO !== deletedAtISO) {
    out = { ...out, deletedAtISO };
  }
  const restoredAtISO = latest(base.restoredAtISO, incoming.restoredAtISO);
  if (restoredAtISO !== undefined && out.restoredAtISO !== restoredAtISO) {
    out = { ...out, restoredAtISO };
  }
  if (winner.textLength !== undefined || loser.textLength !== undefined) {
    const textLength = mergedDocumentLength(out, loser);
    if (textLength !== out.textLength) out = { ...out, textLength };
  }
  return out;
}

/**
 * Union two id-keyed lists into the ONE order every node lands on. The row
 * winner's list leads, then whatever only the loser lists, in its order —
 * so `sources[0]` (the primary every unqualified card ref resolves against)
 * is the winner's primary, and an unkeyed note (which sorts by array
 * position) shows in the same place on every device.
 *
 * When the two rows TIE on their clock there is no real winner — "incoming"
 * is only whichever copy arrived second, and it differs per node (the
 * server's incoming is the push; a peer's is the broadcast). Leading with it
 * would leave each node on its own order for good, so a tie leads with the
 * list whose id sequence sorts first: the same answer in either argument
 * order. An entry both sides hold still merges per entry, the winner taking
 * a per-entry tie.
 *
 * An ABSENT list is the implicit first item (see `deck-items.ts`), which
 * the other side's written list already accounts for: absent yields to
 * whatever is written.
 */
function unionById<T extends MaterialEntry>(
  loser: readonly T[] | undefined,
  winner: readonly T[] | undefined,
  idOf: (entry: T) => string,
  rowsTied: boolean,
): T[] | undefined {
  if (!loser) return winner ? [...winner] : undefined;
  if (!winner) return [...loser];
  const loserLeads = rowsTied && idSequence(loser, idOf) < idSequence(winner, idOf);
  const out: T[] = [];
  const at = new Map<string, number>();
  const place = (entry: T, fromWinner: boolean) => {
    const id = idOf(entry);
    const pos = at.get(id);
    if (pos === undefined) {
      at.set(id, out.length);
      out.push(entry);
    } else {
      // `mergeMaterialEntry(base, incoming)` ties to `incoming`: keep the
      // winner as the incoming side whichever list is walked first.
      out[pos] = fromWinner
        ? mergeMaterialEntry(out[pos], entry)
        : mergeMaterialEntry(entry, out[pos]);
    }
  };
  for (const entry of loserLeads ? loser : winner) place(entry, !loserLeads);
  for (const entry of loserLeads ? winner : loser) place(entry, loserLeads);
  return out;
}

function idSequence<T>(list: readonly T[], idOf: (entry: T) => string): string {
  return list.map(idOf).join('\u0000');
}

/**
 * Union two materials indexes. Pass the row's LWW loser as `loser` and its
 * winner as `winner`: the winner's order leads and it takes a per-entry
 * tie, the way the rest of the row resolved. `rowsTied` says the two rows'
 * clocks were equal (see `unionById`).
 */
export function mergeDeckMaterialsIndex(
  loser: DeckMaterialsIndex | undefined,
  winner: DeckMaterialsIndex | undefined,
  rowsTied = false,
): DeckMaterialsIndex | undefined {
  if (!loser) return winner;
  if (!winner) return loser;
  const notes = unionById<DeckNoteItem>(
    loser.notes,
    winner.notes,
    (n) => n.noteId,
    rowsTied,
  );
  const mindmaps = unionById<DeckMindmapItem>(
    loser.mindmaps,
    winner.mindmaps,
    (m) => m.mindmapId,
    rowsTied,
  );
  return {
    ...loser,
    ...winner,
    ...(notes ? { notes } : {}),
    ...(mindmaps ? { mindmaps } : {}),
  };
}

/**
 * Union two `sourceId` → ISO maps, keeping the LATER stamp of an id. Used for
 * the removal map (an Undo stamps its entry's restore clock later than the
 * removal it undoes, and a second removal after that must outrank the Undo)
 * and for the first-document stamps (the latest decision to make a document
 * first is the one that stands). A join: either order, any grouping, the
 * same map.
 */
function mergeSourceTombstones(
  a: DeckSourceTombstones | undefined,
  b: DeckSourceTombstones | undefined,
): DeckSourceTombstones | undefined {
  if (!a) return b;
  if (!b) return a;
  const out: DeckSourceTombstones = { ...a };
  for (const [id, at] of Object.entries(b)) {
    if (out[id] === undefined || at > out[id]) out[id] = at;
  }
  return out;
}

/** The fields of a deck row that name the deck itself. */
type DeckRowNames = {
  id?: string;
  deckPaths?: readonly string[];
  deckName?: string;
};

type LegacyRemoval = {
  /** Every key the deck's legacy document has been written under. */
  aliases: ReadonlySet<string>;
  /** The deck's own id — the key the legacy document is kept under. */
  deckId: string | undefined;
  /** The latest removal recorded under one of the deck's own keys. */
  aliasRemoval: string | undefined;
  /** The removal a legacy-shaped entry must be restored after, if any. */
  bar: string | undefined;
};

/**
 * The deck's LEGACY document, as builds before multi-source decks spell it:
 * keyed by the deck itself (its id — the read-side backfill and the old
 * write-back; or its path — share-time hydration) and carrying no `origin`.
 * Every document this build attaches or makes primary carries an `origin`.
 */
function isLegacyShaped(entry: DeckSource, aliases: ReadonlySet<string>): boolean {
  return !entry.origin && aliases.has(entry.sourceId);
}

/** What a legacy-shaped entry describes: its file, else its text. */
function legacyDocumentKey(entry: DeckSource): string {
  return entry.pdfInfo?.filePath ?? `${entry.kind}\u0000${entry.sourceLabel ?? ''}`;
}

/**
 * Fold copies of the deck's legacy document written under DIFFERENT deck
 * keys into one entry. Share-time hydration on an older build keyed it by
 * the deck path, the backfill and attach key it by the deck id; two devices
 * that each wrote one before seeing the other would otherwise leave two
 * identical tiles — and removing either one takes the other with it (both
 * are the legacy document the removal is recorded against). One entry,
 * under the deck id when a copy uses it (else the smallest key), stands in
 * the place of the first copy, so the primary stays the primary. Only the
 * SAME document folds: a different file, or anything this build attached
 * (it carries an `origin`), is never touched.
 */
function foldLegacyTwins(list: DeckSource[], legacy: LegacyRemoval): DeckSource[] {
  const copies = new Map<string, DeckSource[]>();
  for (const entry of list) {
    if (!isLegacyShaped(entry, legacy.aliases)) continue;
    const key = legacyDocumentKey(entry);
    copies.set(key, [...(copies.get(key) ?? []), entry]);
  }
  const keep = new Map<string, DeckSource>();
  for (const [key, group] of copies) {
    if (group.length < 2) continue;
    const kept =
      group.find((entry) => entry.sourceId === legacy.deckId) ??
      [...group].sort((a, b) => (a.sourceId < b.sourceId ? -1 : 1))[0];
    const restoredAtISO = group.reduce<string | undefined>(
      (at, entry) => latest(at, entry.restoredAtISO),
      undefined,
    );
    keep.set(
      key,
      restoredAtISO !== undefined && kept.restoredAtISO !== restoredAtISO
        ? { ...kept, restoredAtISO }
        : kept,
    );
  }
  if (keep.size === 0) return list;
  const out: DeckSource[] = [];
  const placed = new Set<string>();
  for (const entry of list) {
    const key = isLegacyShaped(entry, legacy.aliases) ? legacyDocumentKey(entry) : null;
    const kept = key !== null ? keep.get(key) : undefined;
    if (key === null || !kept) {
      out.push(entry);
    } else if (!placed.has(key)) {
      out.push(kept); // the first copy's place; later copies drop
      placed.add(key);
    }
  }
  return out;
}

/**
 * When the row records that the deck's legacy document was removed, the
 * removal a legacy-shaped entry must be RESTORED after to survive.
 *
 * Why a guard beyond the per-id tombstone: an app build from before
 * multi-source decks reads neither the tombstones nor `sourcesClearedAtISO`.
 * It takes an emptied list for "not filled in yet" and writes the deck's
 * legacy document back — stamped `now` (so its own tombstone does not
 * outrank it), under the deck id while the removal may be keyed by the deck
 * path, or onto a deck whose documents never included it (only the emptied
 * marker records that). The merge runs on the server, so this is the one
 * place that can refuse it for everyone. The bar is the latest of the
 * removals recorded under the deck's own keys and the emptied marker — the
 * later of the two, so which one a copy happened to carry never decides
 * whether the document survives; an Undo stamps `restoredAtISO` after both
 * (`restoreClockAfter`).
 */
function legacyRemovalBar(
  rows: readonly (DeckRowNames & {
    sourceTombstones?: DeckSourceTombstones;
    sourcesClearedAtISO?: string;
  })[],
  tombstones: DeckSourceTombstones | undefined,
): LegacyRemoval {
  const aliases = new Set<string>();
  let marker: string | undefined;
  for (const row of rows) {
    if (row.id) aliases.add(row.id);
    if (row.deckName) aliases.add(row.deckName);
    for (const path of row.deckPaths ?? []) if (path) aliases.add(path);
    marker = latest(marker, row.sourcesClearedAtISO || undefined);
  }
  let aliasRemoval: string | undefined;
  for (const alias of aliases) aliasRemoval = latest(aliasRemoval, tombstones?.[alias]);
  const deckId = rows.find((row) => row.id)?.id;
  return { aliases, deckId, aliasRemoval, bar: latest(aliasRemoval, marker) };
}

/** The stamp that made `entry` the first document, if any. The deck's legacy
 *  document answers to a stamp under any of the deck's own keys — its copies
 *  are written under the id or the path, and fold into one entry. */
function primaryStampOf(
  entry: DeckSource,
  stamps: DeckSourcePrimaryStamps,
  legacy: LegacyRemoval,
): string | undefined {
  if (!isLegacyShaped(entry, legacy.aliases)) return stamps[entry.sourceId];
  let at: string | undefined;
  for (const alias of legacy.aliases) at = latest(at, stamps[alias]);
  return at;
}

/**
 * The list with the document most recently MADE first moved to index 0.
 *
 * Index 0 is not a place like any other: every card ref, map node, quiz
 * attempt and PDF annotation that names no document reads it. Only a write
 * that pins the outgoing first document's records may change it, and each
 * such write stamps the newcomer (`sourcePrimaryStamps`). A copy's ORDER is
 * no such decision — a device that had not seen an Undo, a late-landing
 * first pick or another device's first document still lists its old first
 * document first, and a newer unrelated write from it (a rename, a new note,
 * a card count) would otherwise win the order and re-point every bare record
 * of the deck at another document. So the listed document with the latest
 * stamp leads, whichever row won (a tie: the smaller id); the rest keep the
 * union's order. A list where no listed document carries a stamp (written
 * before stamps existed, or never moved) keeps the union's order, as before.
 */
function stampedFirst(
  list: DeckSource[],
  stamps: DeckSourcePrimaryStamps | undefined,
  legacy: LegacyRemoval,
): DeckSource[] {
  if (!stamps || list.length < 2) return list;
  let best: { index: number; at: string; id: string } | undefined;
  list.forEach((entry, index) => {
    if (isPlaceholderSource(entry)) return;
    const at = primaryStampOf(entry, stamps, legacy);
    if (at === undefined) return;
    if (!best || at > best.at || (at === best.at && entry.sourceId < best.id)) {
      best = { index, at, id: entry.sourceId };
    }
  });
  if (!best || best.index === 0) return list;
  const out = [...list];
  const [first] = out.splice(best.index, 1);
  return [first, ...out];
}

/**
 * Union the deck's DOCUMENTS (`SyncDeckMeta.sources`) the way the materials
 * index is unioned — the list is the only thing that makes an attached
 * document reachable, so a newer write from a device that had not yet seen
 * an attach (a rename, a new note, its own attach) must not drop it.
 *
 *   - `sources[0]` — the primary every unqualified card ref resolves
 *     against — is the listed document most recently made first
 *     ({@link stampedFirst}). The rest of the ORDER follows the row's
 *     winner, then whatever only the loser lists, in its order (a clock tie:
 *     `unionById`); with no stamped document listed, that order decides
 *     index 0 too.
 *   - an entry both sides list merges per entry exactly like a note (edit
 *     clock `updatedAtISO` ?? `addedAtISO`, place clock `movedAtISO`), and
 *     keeps the later restore clock of the two.
 *   - a removal is a tombstone in `sourceTombstones`, not an absence. An
 *     entry survives it only if its ALIVE clock — when it was added, or when
 *     an Undo put it back (`sourceAliveClock`) — is later. The edit clock
 *     never counts: a device that had not seen the removal and renamed the
 *     document (or whose clock runs fast) would otherwise bring it back, as
 *     the primary if it lists it first. The removal is stamped after every
 *     clock the entry carried (`withSourceTombstones`), and an Undo after the
 *     removal (`restoreClockAfter`), so skew cannot flip either of them.
 *   - the deck's LEGACY document, once the row records its removal, comes
 *     back only through Undo ({@link legacyRemovalBar}).
 *   - a placeholder (`manual` / hollow entry) never survives beside a real
 *     document — the rule every attach path applies locally.
 *
 * An ABSENT side yields to the other; both absent stays absent.
 */
function mergeDeckSourceList(
  loser: readonly DeckSource[] | undefined,
  winner: readonly DeckSource[] | undefined,
  tombstones: DeckSourceTombstones | undefined,
  primaryStamps: DeckSourcePrimaryStamps | undefined,
  legacy: LegacyRemoval,
  rowsTied: boolean,
): DeckSource[] | undefined {
  const out = unionById(loser, winner, (s) => s.sourceId, rowsTied);
  if (!out) return undefined;
  // Twins fold BEFORE removals apply: the one entry carries the latest Undo
  // any copy saw, so whether the document survives never depends on which
  // copy happened to be dropped first.
  const alive = foldLegacyTwins(out, legacy).filter((entry) => {
    const removedAt = tombstones?.[entry.sourceId];
    if (removedAt !== undefined && sourceAliveClock(entry) <= removedAt) return false;
    if (legacy.bar !== undefined && isLegacyShaped(entry, legacy.aliases)) {
      return (entry.restoredAtISO ?? '') > legacy.bar;
    }
    return true;
  });
  const listed = alive.some((entry) => !isPlaceholderSource(entry))
    ? alive.filter((entry) => !isPlaceholderSource(entry))
    : alive;
  return stampedFirst(listed, primaryStamps, legacy);
}

/**
 * `deckProperties.mergeEntry`. Row-level LWW on `updatedAtISO` exactly as
 * the engine's default (ties to `incoming`) for every field — plus the
 * materials union and the documents union above. Given the same two rows in
 * either order the result is the same row, so the server, a peer and the
 * pushing client all converge on it — except the progress-reset floor, which
 * only a strictly newer `incoming` can raise over `base`, the server's copy
 * ({@link mergeProgressFloor}).
 */
export function mergeDeckMetaEntry<
  T extends DeckRowNames & {
    updatedAtISO?: string;
    materials?: DeckMaterialsIndex;
    sources?: DeckSource[];
    sourceTombstones?: DeckSourceTombstones;
    sourcePrimaryStamps?: DeckSourcePrimaryStamps;
    sourcesClearedAtISO?: string;
    progressResetAtISO?: string;
  },
>(base: T, incoming: T): T {
  const incomingWins = (incoming.updatedAtISO ?? '') >= (base.updatedAtISO ?? '');
  const rowsTied = (incoming.updatedAtISO ?? '') === (base.updatedAtISO ?? '');
  const winner = incomingWins ? incoming : base;
  const loser = incomingWins ? base : incoming;
  let out = winner;
  const materials = mergeDeckMaterialsIndex(loser.materials, winner.materials, rowsTied);
  if (materials !== undefined) out = { ...out, materials };
  const sourceTombstones = mergeSourceTombstones(
    loser.sourceTombstones,
    winner.sourceTombstones,
  );
  if (sourceTombstones !== undefined) out = { ...out, sourceTombstones };
  // Kept from BOTH rows, whoever won: a copy that never saw a document made
  // first (or an older build that drops the field) must not erase it.
  const sourcePrimaryStamps = mergeSourceTombstones(
    loser.sourcePrimaryStamps,
    winner.sourcePrimaryStamps,
  );
  if (sourcePrimaryStamps !== undefined) out = { ...out, sourcePrimaryStamps };
  const legacy = legacyRemovalBar([loser, winner], sourceTombstones);
  const sources = mergeDeckSourceList(
    loser.sources,
    winner.sources,
    sourceTombstones,
    sourcePrimaryStamps,
    legacy,
    rowsTied,
  );
  if (sources !== undefined) out = { ...out, sources };
  // The emptied marker is kept as the LATER of both rows' markers, whoever
  // won the row: it is what bars an older build's refill of the legacy
  // document (the legacy guard above), and a guard that one copy forgets
  // would let the next merge bring back what this one dropped. Readers only
  // count it on an EMPTY list (`deck/sources/cleared.ts`), so it does no harm
  // beside documents added since.
  //
  // A list THIS merge empties with no marker on either row (two devices each
  // removed one of the last two documents) gets one when the legacy document
  // was among the removals — stamped with that removal, the very clock the
  // legacy guard already measures against, so nothing it decides changes.
  // Builds from before multi-source decks read neither it nor the
  // tombstones; this build's readers accept a tombstone as the evidence too
  // (`deck/sources/cleared.ts`). The marker is never manufactured from any
  // OTHER removal: whether a row is "empty" depends on which copies met
  // first (a copy from before a document was added looks emptied by an
  // unrelated removal), so such a marker would drop a legacy document nobody
  // removed, in some orders and not others. The cost: a deck born with
  // several documents, emptied by two devices at once and then refilled by
  // an older build, shows that refill.
  let clearedAt = [loser.sourcesClearedAtISO, winner.sourcesClearedAtISO]
    .filter((at): at is string => !!at)
    .sort()
    .pop();
  const emptiedHere =
    sources !== undefined &&
    sources.length === 0 &&
    [loser.sources, winner.sources].some((list) => (list?.length ?? 0) > 0);
  if (emptiedHere && clearedAt === undefined) clearedAt = legacy.aliasRemoval;
  if (clearedAt !== undefined && out.sourcesClearedAtISO !== clearedAt) {
    out = { ...out, sourcesClearedAtISO: clearedAt };
  }
  const floor = mergeProgressFloor(base, incoming);
  if (out.progressResetAtISO !== floor) {
    out = { ...out, progressResetAtISO: floor };
    if (floor === undefined) delete out.progressResetAtISO;
  }
  return out;
}

/**
 * The progress-reset floor ("Reset flashcards progress") of a merged row.
 * `base` is the copy the server holds — the stored row on a push, the
 * server's row when a client folds a broadcast or a sync-diff into its own
 * (every caller passes it first). The server keeps the floor sticky (never
 * lower than it stored, a fast clock's floor clamped — `UserDo`'s
 * `progress-floor.ts`), so:
 *   - only a STRICTLY newer `incoming` may move it, and only up — a newer
 *     row that never saw the reset (an older build, a rename made offline)
 *     must not drop it, which whole-row LWW alone would do;
 *   - on a tie or an older `incoming` the server's floor stands, so a
 *     device handed its fast-clock floor back clamped adopts the clamped one
 *     (its row ties the stored one) instead of keeping the future floor and
 *     re-pushing it on every flush.
 * A push that RAISES the floor without being newer is re-stamped past the
 * stored row before this runs (`stickProgressFloorOnDeckPush`).
 */
function mergeProgressFloor(
  base: { updatedAtISO?: string; progressResetAtISO?: string },
  incoming: { updatedAtISO?: string; progressResetAtISO?: string },
): string | undefined {
  const kept = base.progressResetAtISO || undefined;
  const pushed = incoming.progressResetAtISO || undefined;
  if (!((incoming.updatedAtISO ?? '') > (base.updatedAtISO ?? ''))) return kept;
  return kept && (!pushed || kept > pushed) ? kept : pushed;
}
